import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Rocket, Star, Zap, Crown, Loader2, AlertTriangle, Tag, X } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const PLAN_META = {
  free:      { price: 0,     listings: 3,     icon: Star,   accent: "#64748b" },
  start:     { price: 9.9,   listings: 50,    icon: Zap,    accent: "#0ea5e9" },
  pro:       { price: 19.9,  listings: 150,   icon: Rocket, accent: "#6366f1" },
  unlimited: { price: 99.9,  listings: null,  icon: Crown,  accent: "#a855f7" },
};
const ORDER = ["free", "start", "pro", "unlimited"];

const TX = {
  es: {
    title: "Elige tu plan", subtitle: "Sin comisiones por venta. Solo pagas tu suscripción mensual.",
    perMonth: "/mes", listings: (n) => n === null ? "Anuncios ilimitados" : `${n} anuncios activos`,
    noCommission: "0% de comisión en ventas", directPay: "Cobro directo en tu cuenta bancaria",
    proBadge: "Perfil de Profesional Verificado + directorio de servicios",
    current: "Tu plan actual", choose: "Elegir plan", upgrade: "Cambiar a este plan", processing: "Redirigiendo al pago…",
    pendingTitle: "Completa el pago de tu plan", pendingDesc: "Tu cuenta está pendiente hasta que completes el pago de tu suscripción.",
    payNow: "Pagar ahora", freeSelected: "Plan Free activado", cardBizum: "Pago con tarjeta o Bizum (Stripe)",
    mostPopular: "Más popular",
  },
  en: {
    title: "Choose your plan", subtitle: "No sales commission. You only pay your monthly subscription.",
    perMonth: "/mo", listings: (n) => n === null ? "Unlimited listings" : `${n} active listings`,
    noCommission: "0% sales commission", directPay: "Direct payment to your bank account",
    proBadge: "Verified Professional profile + services directory",
    current: "Your current plan", choose: "Choose plan", upgrade: "Switch to this plan", processing: "Redirecting to payment…",
    pendingTitle: "Complete your plan payment", pendingDesc: "Your account is pending until you complete your subscription payment.",
    payNow: "Pay now", freeSelected: "Free plan activated", cardBizum: "Pay by card or Bizum (Stripe)",
    mostPopular: "Most popular",
  },
  it: {
    title: "Scegli il tuo piano", subtitle: "Nessuna commissione sulle vendite. Paghi solo l'abbonamento mensile.",
    perMonth: "/mese", listings: (n) => n === null ? "Annunci illimitati" : `${n} annunci attivi`,
    noCommission: "0% di commissioni sulle vendite", directPay: "Pagamento diretto sul tuo conto corrente",
    proBadge: "Profilo Professionista Verificato + directory servizi",
    current: "Il tuo piano attuale", choose: "Scegli piano", upgrade: "Passa a questo piano", processing: "Reindirizzamento al pagamento…",
    pendingTitle: "Completa il pagamento del tuo piano", pendingDesc: "Il tuo account è in attesa finché non completi il pagamento dell'abbonamento.",
    payNow: "Paga ora", freeSelected: "Piano Free attivato", cardBizum: "Pagamento con carta o Bizum (Stripe)",
    mostPopular: "Più popolare",
  },
};

export default function Plans() {
  const { lang } = useI18n();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const tx = TX[lang] || TX.es;
  const [loading, setLoading] = useState("");
  const [paypalOn, setPaypalOn] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponErr, setCouponErr] = useState("");
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const currentPlan = user?.plan || "free";
  const pending = user?.plan_status === "pending_payment";

  React.useEffect(() => {
    api.get("/payments/providers").then(({ data }) => setPaypalOn(!!data.paypal)).catch(() => {});
  }, []);

  const cLabel = { es: { label: "¿Tienes un código de cupón?", ph: "Introduce tu código", apply: "Aplicar", remove: "Quitar", applied: "Cupón aplicado", notFor: "No aplicable a este plan" },
    en: { label: "Do you have a coupon code?", ph: "Enter your code", apply: "Apply", remove: "Remove", applied: "Coupon applied", notFor: "Not applicable to this plan" },
    it: { label: "Hai un codice coupon?", ph: "Inserisci il codice", apply: "Applica", remove: "Rimuovi", applied: "Coupon applicato", notFor: "Non applicabile a questo piano" } }[lang] || {};

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) return;
    setCheckingCoupon(true); setCouponErr("");
    try {
      const { data } = await api.post("/coupons/validate", { code });
      if (data.valid) { setCoupon(data); setCouponErr(""); }
      else { setCoupon(null); setCouponErr(data.error || "Cupón no válido"); }
    } catch (e) { setCoupon(null); setCouponErr(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setCheckingCoupon(false); }
  };
  const removeCoupon = () => { setCoupon(null); setCouponInput(""); setCouponErr(""); };

  const couponAppliesTo = (planId) => coupon && (coupon.plan === "all" || coupon.plan === planId);
  const discountedPrice = (planId, price) => {
    if (!couponAppliesTo(planId) || price <= 0) return null;
    const d = coupon.discount_type === "percent" ? price * (coupon.discount_value / 100) : coupon.discount_value;
    return Math.max(0, Math.round((price - d) * 100) / 100);
  };
  const fmtPrice = (p) => (p === 0 ? "0€" : `${p.toString().replace(".", ",")}€`);

  const planName = { free: "Free", start: "Start", pro: "Pro", unlimited: "Unlimited" };

  const choose = async (planId, provider = "stripe") => {
    if (!user) { navigate("/auth"); return; }
    setLoading(planId + provider);
    try {
      const { data } = await api.post("/subscription/checkout", {
        plan: planId, provider, origin_url: window.location.origin,
        coupon_code: couponAppliesTo(planId) ? coupon.code : undefined,
        business_name: user.business_name, business_category: user.business_category,
      });
      if (data.free) { await refresh(); toast.success(tx.freeSelected); setLoading(""); navigate("/dashboard"); return; }
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      setLoading("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); setLoading(""); }
  };

  return (
    <div className="px-4 py-8 max-w-6xl mx-auto" data-testid="plans-page">
      {pending && (
        <div data-testid="plan-pending-banner" className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-900">{tx.pendingTitle}</p>
            <p className="text-sm text-amber-800">{tx.pendingDesc}</p>
          </div>
        </div>
      )}
      <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-center text-slate-900">{tx.title}</h1>
      <p className="text-center text-slate-500 mt-2 mb-6">{tx.subtitle}</p>

      <div className="max-w-md mx-auto mb-8" data-testid="coupon-box">
        <label className="text-sm font-medium text-slate-600 flex items-center gap-1.5 mb-1.5"><Tag className="h-4 w-4 text-[#008080]" />{cLabel.label}</label>
        {coupon ? (
          <div data-testid="coupon-applied" className="flex items-center justify-between rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2.5">
            <span className="text-sm text-emerald-800 font-semibold flex items-center gap-1.5"><Check className="h-4 w-4" />{cLabel.applied}: <b>{coupon.code}</b> ({coupon.discount_type === "percent" ? `-${coupon.discount_value}%` : `-${coupon.discount_value}€`})</span>
            <button data-testid="coupon-remove" onClick={removeCoupon} className="text-slate-400 hover:text-rose-500"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Input data-testid="coupon-input" value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === "Enter") applyCoupon(); }} placeholder={cLabel.ph} className="h-11" />
            <Button data-testid="coupon-apply" onClick={applyCoupon} disabled={checkingCoupon || !couponInput.trim()} className="h-11 bg-[#008080] hover:bg-[#006666] px-5">
              {checkingCoupon ? <Loader2 className="h-4 w-4 animate-spin" /> : cLabel.apply}
            </Button>
          </div>
        )}
        {couponErr && <p data-testid="coupon-error" className="text-xs text-rose-500 mt-1.5">{couponErr}</p>}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {ORDER.map((planId) => {
          const meta = PLAN_META[planId];
          const Icon = meta.icon;
          const isCurrent = currentPlan === planId && !pending;
          const highlight = planId === "pro";
          return (
            <div key={planId} data-testid={`plan-card-${planId}`}
              className={`relative flex flex-col rounded-3xl border p-6 bg-white transition-shadow hover:shadow-xl ${highlight ? "border-indigo-300 ring-2 ring-indigo-200 shadow-lg" : "border-slate-200"}`}>
              {highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-bold uppercase tracking-wide px-3 py-1 rounded-full bg-indigo-500 text-white">{tx.mostPopular}</span>}
              <span className="h-11 w-11 grid place-items-center rounded-2xl" style={{ background: `${meta.accent}1a`, color: meta.accent }}><Icon className="h-6 w-6" /></span>
              <h3 className="font-display font-bold text-xl mt-4">{planName[planId]}</h3>
              <div className="mt-2 flex items-baseline gap-1 flex-wrap">
                {(() => {
                  const disc = discountedPrice(planId, meta.price);
                  if (disc != null) return (<>
                    <span className="font-display font-extrabold text-3xl text-[#008080]" data-testid={`plan-discounted-${planId}`}>{fmtPrice(disc)}</span>
                    <span className="text-lg text-slate-400 line-through ml-1">{fmtPrice(meta.price)}</span>
                    <span className="text-sm text-slate-400">{tx.perMonth}</span>
                  </>);
                  return (<>
                    <span className="font-display font-extrabold text-3xl text-slate-900">{meta.price === 0 ? "0€" : `${meta.price.toString().replace(".", ",")}€`}</span>
                    {meta.price > 0 && <span className="text-sm text-slate-400">{tx.perMonth}</span>}
                  </>);
                })()}
              </div>
              <ul className="mt-4 space-y-2 text-sm text-slate-600 flex-1">
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />{tx.listings(meta.listings)}</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />{tx.noCommission}</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />{tx.directPay}</li>
                {planId === "unlimited" && <li className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />{tx.proBadge}</li>}
              </ul>
              {meta.price > 0 && <p className="text-[11px] text-slate-400 mt-3">{tx.cardBizum}</p>}
              <Button
                data-testid={`plan-choose-${planId}`}
                disabled={isCurrent || loading.startsWith(planId)}
                onClick={() => choose(planId, "stripe")}
                className={`w-full mt-4 h-11 ${isCurrent ? "bg-slate-100 text-slate-500 hover:bg-slate-100" : highlight ? "bg-indigo-500 hover:bg-indigo-600" : "bg-[#008080] hover:bg-[#006666]"}`}>
                {loading === planId + "stripe" ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />{tx.processing}</>
                  : isCurrent ? tx.current
                  : pending && currentPlan === planId ? tx.payNow
                  : meta.price > 0 ? (lang === "it" ? "Paga con carta / Bizum" : lang === "en" ? "Pay by card / Bizum" : "Pagar con tarjeta / Bizum")
                  : currentPlan === "free" ? tx.choose : tx.upgrade}
              </Button>
              {paypalOn && meta.price > 0 && !isCurrent && (
                <Button
                  data-testid={`plan-choose-${planId}-paypal`}
                  disabled={loading.startsWith(planId)}
                  onClick={() => choose(planId, "paypal")}
                  className="w-full mt-2 h-11 bg-[#ffc439] hover:bg-[#f0b90b] text-[#003087] font-bold">
                  {loading === planId + "paypal" ? <Loader2 className="h-4 w-4 animate-spin" /> : "PayPal"}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
